import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  UncontrolledTooltip,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { utils as xlsxUtils, writeFile as xlsxWriteFile, read as xlsxRead } from 'xlsx';
import axiosPrivate from 'config/axiosPrivate';
import { useDispatch } from 'react-redux';
import GapGenerationModal from 'components/shared/modal/GapGenerationModal';
import { fetchAdminClientTextOrderDisplay as fetchAdminTextOrderDisplay } from 'store/AdminClient/OrderDisplay/features';
import './RedactionModal.css';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';

const EnrichissementModal = ({
  isOpen,
  onClose,
  orderId,
  title,
  aiAgentId, orderWebsite, language, country,
  orderTextAmount,
  t = (key) => key,
}) => {
  const [formData, setFormData] = React.useState({
    keyword: '',
    url: '',
    semanticGap: '',
    internalLinking: '',
    existingText: '',
  });
  const [errors, setErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState('danger');
  const [isRetrieving, setIsRetrieving] = React.useState(false);
  const [isGapModalOpen, setIsGapModalOpen] = React.useState(false);
  const [gapMode, setGapMode] = React.useState('aqisis');
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = React.useState(null);
  const [remainingCredits, setRemainingCredits] = React.useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = React.useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = React.useState(false);
  const [isContentLimitModalOpen, setIsContentLimitModalOpen] = React.useState(false);

  const [selectedFile, setSelectedFile] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('tab1');
  const [filePreviewData, setFilePreviewData] = React.useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 5;
  const [isExistingTextVisible, setIsExistingTextVisible] = React.useState(false);
  const [currentGeneratedTextsCount, setCurrentGeneratedTextsCount] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [notificationMessage, setNotificationMessage] = React.useState('');
  const notificationTimeoutRef = React.useRef(null);

  const normalizeUrl = (input) => {
    return String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };

  const checkPremiumWebsiteQuota = async () => {
    const normalizedOrderWebsite = normalizeUrl(orderWebsite);
    if (!normalizedOrderWebsite) return true;

    try {
      const websitesResponse = await axiosPrivate.get('/project/website/all');
      const websites = Array.isArray(websitesResponse?.data) ? websitesResponse.data : [];
      const matchingWebsite = websites.find((websiteItem) => {
        const websiteUrl = normalizeUrl(websiteItem?.urlSite || websiteItem?.url || websiteItem?.name || '');
        return websiteUrl === normalizedOrderWebsite;
      });

      if (!matchingWebsite?.id) return true;

      const quotasResponse = await axiosPrivate.get(`/project/website-quotas/${matchingWebsite.id}`);
      const maxTexts = Number(quotasResponse?.data?.maxTexts ?? 0);
      const usedTexts = Number(quotasResponse?.data?.usedTexts ?? 0);

      return maxTexts - usedTexts > 0;
    } catch {
      return true;
    }
  };

  // Update existing text visibility based on URL
  React.useEffect(() => {
    const hasUrl = formData.url && formData.url.trim() !== '';
    setIsExistingTextVisible(hasUrl);
  }, [formData.url]);

  // Polling states
  const dispatch = useDispatch();
  const [pollingError, setPollingError] = React.useState(null);
  const [jobStatuses, setJobStatuses] = React.useState([]);
  const [fetchedJobs, setFetchedJobs] = React.useState(new Set());
  const pollingIntervalRef = React.useRef(null);


  const generationStorageKey = orderId ? `AC_Generation_${orderId}` : null;
  const generationEventName = 'ac-generation-changed';
  const setGenerationStatus = (isGenerating) => {
    if (!generationStorageKey) return;
    try {
      window.localStorage.setItem(generationStorageKey, isGenerating ? '1' : '0');
      window.dispatchEvent(new Event(generationEventName));
    } catch { }
  };

  // Polling functions
  const checkJobStatus = async () => {
    if (!orderId) return;

    try {
      const response = await axiosPrivate.get(`/project/job-status/${orderId}`);

      if (response.status === 204) {
        try {
          if (
            window.localStorage.getItem('AdminClient_ProjectDisplay_id') ==
            orderId
          ) {
            await dispatch(fetchAdminTextOrderDisplay(orderId));
          }
          setPollingError(null);
        } catch {
          setPollingError(t('Erreur lors de la récupération des textes'));
        } finally {
          stopPolling();
        }
        return;
      }

      const currentStatuses = response.data || [];
      setJobStatuses(currentStatuses);

      const newCompletedJobs = currentStatuses.filter(
        job => job.jobStatus === 'COMPLETED' && !fetchedJobs.has(job.keyword)
      );

      if (newCompletedJobs.length > 0) {
        try {
          if (
            window.localStorage.getItem('AdminClient_ProjectDisplay_id') ==
            orderId
          ) {
            await dispatch(fetchAdminTextOrderDisplay(orderId));
          }

          setFetchedJobs(prev => {
            const updated = new Set(prev);
            newCompletedJobs.forEach(job => updated.add(job.keyword));
            return updated;
          });
          setPollingError(null);
        } catch {
          setPollingError(t('Erreur lors de la récupération des textes'));
        }
      }

      // On ne stoppe plus le polling sur simple "COMPLETED" : on attend la réponse 204 de l'API
    } catch (error) {
      setPollingError(t('Erreur lors de la surveillance des jobs'));
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return;
    if (!orderId) return;

    checkJobStatus();
    setGenerationStatus(true);
    pollingIntervalRef.current = setInterval(checkJobStatus, 10000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setGenerationStatus(false);
    // La génération est considérée comme totalement terminée (204 reçu), on peut masquer la notification globale
    setNotificationMessage('');
  };


  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ];

      if (
        allowedTypes.includes(file.type) ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls')
      ) {
        const fakeEvent = {
          target: {
            files: [file],
            value: '',
          },
        };
        handleFileChange(fakeEvent);
      } else {
        setAlertMessage(
          t(
            'Format de fichier non supporté. Veuillez sélectionner un fichier .xlsx, .xls ou .csv'
          )
        );
        setAlertType('danger');
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreviewData([]);
    setCurrentPage(1);
    setErrors({});
    setAlertMessage('');
    const fileInput = document.getElementById('smartBatchUpload');
    if (fileInput) fileInput.value = '';
  };


  React.useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // Synchroniser la notification "Génération en cours..." avec l'état global stocké en localStorage
  React.useEffect(() => {
    if (!generationStorageKey) return;

    const syncFromStorage = () => {
      try {
        const val = window.localStorage.getItem(generationStorageKey);
        if (val === '1') {
          setNotificationMessage(t('Génération en cours...'));
        } else {
          setNotificationMessage('');
        }
      } catch {
        // ignore
      }
    };

    // Synchronisation immédiate à l'ouverture / au montage
    syncFromStorage();

    // Écoute des changements globaux (lancés par d'autres composants)
    const handler = () => syncFromStorage();
    window.addEventListener(generationEventName, handler);

    return () => {
      window.removeEventListener(generationEventName, handler);
    };
  }, [generationStorageKey, t]);

  const getExcelColumnWidth = (header, minWidth = 25) => ({
    wch: Math.max(String(header).length + 10, minWidth),
  });

  const handleDownloadTemplate = () => {
    try {
      const workBook = xlsxUtils.book_new();

      const keywordHeader = 'Mots cle (Obligatoire)';
      const urlHeader = 'Url (Obligatoire)';
      const gapHeader = 'Gap semantique (Optionnel)';
      const maillageHeader = 'Maillage interne (Optionnel)';
      const existingTextHeader = 'Texte existant (Optionnel)';

      const headersData = [
        {
          [keywordHeader]: '',
          [urlHeader]: '',
          [gapHeader]: '',
          [maillageHeader]: '',
          [existingTextHeader]: ''
        }
      ];

      const columnWidths = [
        getExcelColumnWidth(keywordHeader),
        getExcelColumnWidth(urlHeader),
        getExcelColumnWidth(gapHeader, 35),
        getExcelColumnWidth(maillageHeader, 35),
        getExcelColumnWidth(existingTextHeader, 40),
      ];

      const xlsxSheet = xlsxUtils.json_to_sheet(headersData);
      xlsxSheet['!cols'] = columnWidths;
      xlsxUtils.book_append_sheet(workBook, xlsxSheet, 'Template');
      xlsxWriteFile(workBook, 'modele_enrichissement_smart.xlsx');
    } catch (e) {
      console.error('Template download failed:', e);
    }
  };

  const fetchCurrentTextsCount = React.useCallback(async () => {
    if (!orderId || !orderTextAmount) return;
    try {
      const response = await axiosPrivate.get(`/texts/external/by-project/${orderId}`);
      if (response.status === 200) {
        const texts = response.data || [];
        setCurrentGeneratedTextsCount(texts.length);
      }
    } catch (error) {
      console.error('Error fetching texts count:', error);
    }
  }, [orderId, orderTextAmount]);

  React.useEffect(() => {
    if (isOpen && orderId) {
      fetchCurrentTextsCount();
    }
  }, [isOpen, orderId, fetchCurrentTextsCount]);

  // Réinitialiser le formulaire quand le modal s'ouvre
  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  React.useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);

        const total = Number(data?.totalCredits ?? 0);
        const used = Number(data?.usedCredits ?? 0);
        if (!Number.isNaN(total) && !Number.isNaN(used)) {
          setRemainingCredits(Math.max(total - used, 0));
        } else {
          setRemainingCredits(null);
        }
      } catch (error) {
        if (isMounted) {
          setGapAnalysisEnabled(false);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      if (allowedTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
        setAlertMessage('');
        setCurrentPage(1);
        setErrors({});

        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = xlsxRead(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = xlsxUtils.sheet_to_json(worksheet, { header: 1 });

            if (jsonData.length > 1) {
              const rows = jsonData.slice(1).filter(row => row.length > 0 && row[0]);

              // Validation : vérifier si le nombre de textes dépasse le nombre restant
              if (orderTextAmount && rows.length > 0) {
                const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
                if (rows.length > remainingTexts) {
                  setErrors({
                    file: t(`Actuellement, le fichier comprend ${rows.length} textes ; il reste cependant ${remainingTexts} texte(s) à générer.`)
                  });
                  setAlertMessage(t(`Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`));
                  setAlertType('danger');
                  setSelectedFile(null);
                  setFilePreviewData([]);
                  e.target.value = '';
                  return;
                }
              }

              const formattedData = rows.map(row => ({
                keyword: row[0] || '',
                url: row[1] || '',
                gap: row[2] || '',
                maillage: row[3] || '',
                existingText: row[4] || ''
              }));

              setFilePreviewData(formattedData);


              if (notificationTimeoutRef.current) {
                clearTimeout(notificationTimeoutRef.current);
              }
              setNotificationMessage(t('Modèle Excel téléchargé !'));
              notificationTimeoutRef.current = setTimeout(() => {
                setNotificationMessage('');
              }, 3000);
            }
          } catch (error) {
            console.error('Erreur lors de la lecture du fichier:', error);
            setAlertMessage(t('Erreur lors de la lecture du fichier'));
            setAlertType('danger');
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setAlertMessage(t('Format de fichier non supporté. Veuillez sélectionner un fichier .xlsx, .xls ou .csv'));
        setAlertType('danger');
        e.target.value = '';
      }
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      return { ...prev, [name]: value };
    });

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (alertMessage) setAlertMessage('');
  };

  const validateForm = () => {
    const newErrors = {};
    const currentOption = activeTab === 'tab1' ? 'option1' : 'option2';

    if (currentOption === 'option1') {
      if (!formData.keyword.trim()) newErrors.keyword = t('Le mot clé est obligatoire');
      if (!formData.url.trim()) newErrors.url = t("L'URL est obligatoire");
      if (formData.url.trim() && !isValidUrl(formData.url)) newErrors.url = t("Format d'URL invalide");
    } else if (currentOption === 'option2') {
      if (!selectedFile) {
        newErrors.file = t('Veuillez sélectionner un fichier Excel');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const currentOption = activeTab === 'tab1' ? 'option1' : 'option2';

    if (currentOption === 'option1') {
      return (
        formData.keyword.trim() &&
        formData.url.trim() &&
        isValidUrl(formData.url)
      );
    } else if (currentOption === 'option2') {
      return selectedFile !== null;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const canCreatePremiumContent = await checkPremiumWebsiteQuota();
    if (!canCreatePremiumContent) {
      handleModalClose();
      setIsContentLimitModalOpen(true);
      return;
    }

    if (remainingCredits !== null && remainingCredits <= 0) {
     
      handleModalClose();
      setIsCreditsLimitModalOpen(true);
      return;
    }

    if (!validateForm()) {
      setAlertMessage(t('Veuillez corriger les erreurs dans le formulaire'));
      setAlertType('danger');
      return;
    }

    if (!orderId) {
      setAlertMessage(t('ID du projet manquant'));
      setAlertType('danger');
      return;
    }

    setIsLoading(true);
    setAlertMessage('');

    try {
      if (activeTab === 'tab1') {
        // Optimisation d'un seul texte
        const gapArray = formData.semanticGap.trim()
          ? formData.semanticGap
            .split(/[,\n]/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
          : [];

        const maillageInterneArray = formData.internalLinking.trim()
          ? formData.internalLinking
            .split(/[,\n]/)
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
          : [];

        const requestBody = {
          keyword: formData.keyword.trim(),
          agentId: aiAgentId || '',
          urlPage: formData.url.trim() || undefined,
          gap: gapArray,
          maillageInterne: maillageInterneArray,
          content: formData.existingText.trim() || undefined,
        };

        setAlertMessage(t('Enrichissement en cours...'));
        setAlertType('info');
        setNotificationMessage(t('Génération en cours...'));

        await axiosPrivate.post(
          `/project/generation/optimize-one-text/${orderId}`,
          requestBody
        );

        setAlertMessage(t('Enrichissement en cours... Surveillance des jobs.'));
        setAlertType('info');
        startPolling();
        resetForm();
        onClose();
      } else {
        // Optimisation par lot (batch)
        if (!selectedFile) {
          setAlertMessage(t('Veuillez sélectionner un fichier Excel'));
          setAlertType('danger');
          setIsLoading(false);
          return;
        }

        // Vérification finale avant soumission
        if (orderTextAmount && filePreviewData.length > 0) {
          const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
          if (filePreviewData.length > remainingTexts) {
            setAlertMessage(t(`Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`));
            setAlertType('danger');
            setIsLoading(false);
            return;
          }
        }

        setAlertMessage(t('Traitement du fichier en cours...'));
        setAlertType('info');
        setNotificationMessage(t('Génération en cours...'));

        const formDataToSend = new FormData();
        formDataToSend.append('excelFile', selectedFile);

        await axiosPrivate.post(
          `/project/generation/optimize-batch-text/${orderId}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        setAlertMessage(t('Traitement du fichier en cours... Surveillance des jobs.'));
        setAlertType('info');
        startPolling();
        resetForm();
        onClose();
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('Erreur lors de la soumission');
      setAlertMessage(errorMsg);
      setAlertType('danger');
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      keyword: '',
      url: '',
      semanticGap: '',
      internalLinking: '',
      existingText: ''
    });
    setErrors({});
    setAlertMessage('');
    setSelectedFile(null);
    setFilePreviewData([]);
    setCurrentPage(1);
    setActiveTab('tab1');
    setCurrentGeneratedTextsCount(0);
    const fileInput = document.getElementById('smartBatchUpload');
    if (fileInput) fileInput.value = '';
    setIsExistingTextVisible(true);
    setNotificationMessage('');
  };

  const handleModalClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  const handleSemanticGapChangeFromModal = (event) => {
    const value = event?.target?.value || '';
    setFormData((prev) => ({ ...prev, semanticGap: value }));
  };


  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleModalClose}
        size="lg"
        className="redaction-modal"
        backdrop={isLoading ? 'static' : true}
        keyboard={!isLoading}
      >
        <ModalHeader toggle={!isLoading ? handleModalClose : undefined}>
          {title ? title : t('Enrichissement de contenu')}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {alertMessage && (
              <Alert color={alertType} className="mb-3">
                {alertMessage}
              </Alert>
            )}
            {pollingError && (
              <Alert color="warning" className="mb-3">
                {pollingError}
              </Alert>
            )}
            {jobStatuses.length > 0 && activeTab === 'tab1' && (
              <Alert color="info" className="mb-3">
                <div>
                  <strong>{t('Statut des jobs')}:</strong>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    {jobStatuses.map((job, index) => (
                      <li key={index}>
                        {job.keyword}: {job.jobStatus}
                      </li>
                    ))}
                  </ul>
                </div>
              </Alert>
            )}

            <div className="modal-option-row mb-3">
              <button
                type="button"
                className={`option-card ${activeTab === 'tab1' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('tab1');
                  setSelectedFile(null);
                  setFilePreviewData([]);
                  setCurrentPage(1);
                  const fileInput = document.getElementById('smartBatchUpload');
                  if (fileInput) fileInput.value = '';
                }}
              >
                <img
                  src={activeTab === 'tab1' ? 'Images/one-text-acti.png' : 'Images/one-text-desact.png'}
                  alt="One text icon"
                  style={{ width: '40px', height: '40px', marginRight: '12px' }}
                />
                <div className="option-text" style={{ marginLeft: '-50px' }}>
                  <div className="option-title">
                    {t('Enrichir un contenu')}
                  </div>
                  <div className="option-subtitle">
                    {t('Optimisez un texte spécifique avec un formulaire guidé')}
                  </div>
                </div>
                <div className={`option-check ${activeTab === 'tab1' ? 'checked' : ''}`} />
              </button>

              <button
                type="button"
                className={`option-card ${activeTab === 'tab2' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('tab2');
                }}
              >
                <img
                  src={activeTab === 'tab2' ? 'Images/act-batch-text.png' : 'Images/batch-icon-desact.png'}
                  alt="Batch icon"
                  style={{ width: '40px', height: '40px', marginRight: '-70px' }}
                />
                <div className="option-text" style={{ marginLeft: '70px' }}>
                  <div className="option-title" >
                    {t('Enrichir un lot de contenus')}
                  </div>
                  <div className="option-subtitle" >
                    {t('Générez plusieurs enrichissements en important un fichier Excel')}
                  </div>
                </div>
                <div className={`option-check ${activeTab === 'tab2' ? 'checked' : ''}`} />
              </button>
            </div>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="tab1">
                <FormGroup>
                  <Label for="keyword" className="required-field">
                    {t('Mot clé')}
                  </Label>
                  <Input
                    type="text"
                    id="keyword"
                    name="keyword"
                    value={formData.keyword}
                    onChange={handleInputChange}
                    placeholder={t('Saisissez le mot-clé principal de votre contenu.')}
                    disabled={isLoading || activeTab !== 'tab1'}
                    invalid={!!errors.keyword}
                  />
                  {errors.keyword && <div className="invalid-feedback d-block">{errors.keyword}</div>}
                </FormGroup>

                <FormGroup>
                  <Label for="url" className="required-field">{t('Url page')}</Label>
                  <Input
                    type="text"
                    id="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder={t("Entrez l'URL cible de votre contenu.")}
                    disabled={isLoading || activeTab !== 'tab1'}
                    invalid={!!errors.url}
                  />
                  {errors.url && <div className="invalid-feedback d-block">{errors.url}</div>}
                </FormGroup>

                {formData.url && formData.url.trim() !== '' && (
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label className="mb-0" for="existingText">
                        {t('Texte existant')}
                      </Label>
                      <Button
                        type="button"
                        color="link"
                        className="p-0 existing-text-toggle-icon"
                        onClick={() => setIsExistingTextVisible((prev) => !prev)}
                        aria-label={isExistingTextVisible ? t('Replier le texte existant') : t('Déplier le texte existant')}
                      >
                        <i className={`bi ${isExistingTextVisible ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: '1.1rem' }} />
                      </Button>
                    </div>
                    {isExistingTextVisible && (
                      <Input
                        type="textarea"
                        id="existingText"
                        name="existingText"
                        value={formData.existingText}
                        onChange={handleInputChange}
                        placeholder={t('Collez le texte existant associé à cette URL.')}
                        disabled={isLoading || activeTab !== 'tab1'}
                        invalid={!!errors.existingText}
                        rows="5"
                      />
                    )}
                    {errors.existingText && <div className="invalid-feedback d-block">{errors.existingText}</div>}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label for="semanticGap">
                    {t('GAP sémantique')}
                    <span id="gapTooltip" className="ms-2" style={{ cursor: "pointer", color: "#ef4444" }}>ⓘ</span>
                  </Label>
                  <Input
                    type="textarea"
                    id="semanticGap"
                    name="semanticGap"
                    value={formData.semanticGap}
                    onChange={handleInputChange}
                    placeholder={t('keyword1 (2),\nkeyword2 (1),\nkeyword3 (1),\nkeyword4 (2),\n.....')}
                    disabled={isLoading || activeTab !== 'tab1'}
                    rows="4"
                    className="semantic-gap-textarea"
                  />
                  {errors.semanticGap && <div className="invalid-feedback d-block">{errors.semanticGap}</div>}
                  <UncontrolledTooltip
                    placement="right"
                    target="gapTooltip"
                    className="custom-redac-tooltip"
                  >
                    {t('Saisissez chaque mot-clé suivi de sa fréquence entre parenthèses, séparés par une virgule ou un retour à la ligne. \n Exemple :\n chaussures(3), robe(2), pantalon(1)')}
                  </UncontrolledTooltip>

                  <div
                    className="semantic-gap-actions"
                    style={{ marginLeft: '150px' }}
                  >
                    <button
                      type="button"
                      className="gap-light-btn"
                      onClick={() => {
                        if (gapAnalysisEnabled !== true) return;
                        setIsGapModalOpen(true);
                      }}
                      disabled={gapAnalysisEnabled !== true || isLoading || isRetrieving}
                    >
                      <span className="gap-refresh-icon">
                        <img
                          src="/Images/gap-icon-gene.png"
                          alt={t('Générer une couverture sémantique')}
                          style={{ width: 16, height: 16, marginLeft: -6 }}
                        />
                      </span>
                      <span className="gap-refresh-label">
                        {t('Générer une couverture sémantique')}
                      </span>
                    </button>
                  </div>

                </FormGroup>

                <FormGroup>
                  <Label for="internalLinking">
                    {t('Maillage interne')}
                  </Label>
                  <Input
                    type="textarea"
                    id="internalLinking"
                    name="internalLinking"
                    value={formData.internalLinking}
                    onChange={handleInputChange}
                    placeholder={t('URLs des pages à lier (séparées par des virgules)')}
                    rows="3"
                    disabled={isLoading || activeTab !== 'tab1'}
                  />
                </FormGroup>
              </TabPane>

              <TabPane tabId="tab2">
                <div className="batch-upload-container">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '20px' }}>
                    <img
                      src="Images/icon-excel-import.png"
                      alt="excel"
                      style={{ width: '40px', height: '40px', marginTop: '20px' }}
                    />
                    <p style={{ fontSize: '14px', marginTop: '30px' }}>
                      {t("Besoin d'aide pour préparer votre fichier ?")}
                    </p>
                  </div>

                  <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
                    <label
                      htmlFor="smartBatchUpload"
                      className={`upload-button ${selectedFile ? 'file-selected' : ''}`}
                    >
                    </label>
                    <input
                      id="smartBatchUpload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      disabled={isLoading}
                    />
                    <span
                      onClick={handleDownloadTemplate}
                      role="button"
                      style={{ cursor: 'pointer' }}
                      title={t('Télécharger le modèle (Excel)')}
                    >
                      <button
                        type="button"
                        style={{
                          backgroundColor: '#FDFDFC',
                          color: '#2F979E',
                          border: '2px solid #2F979E',
                          height: '40px',
                          width: '250px',
                          borderRadius: '8px',
                          marginLeft: '55px',
                        }}
                      >
                        {t('Télécharger le modèle (Excel)')}
                      </button>
                    </span>
                  </div>

                  {errors.file && (
                    <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                      {errors.file}
                    </div>
                  )}
                </div>

                {!selectedFile ? (
                  // Zone de drag & drop quand aucun fichier n'est sélectionné
                  <div
                    className={`batch1-upload-container ${isDragging ? 'dragging' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: isDragging ? '2px dashed #2F979E' : '2px dashed #E0E0E0',
                      backgroundColor: isDragging ? '#f0f9fa' : '#FAFAFA',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <div>
                      <img
                        src="Images/batch-icon-desact.png"
                        alt="Upload icon"
                        style={{
                          width: '60px',
                          height: '60px',
                          marginBottom: '20px',
                          borderRadius: '50%',
                          opacity: isDragging ? 0.7 : 1,
                        }}
                      />
                      <p
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          marginBottom: '10px',
                          color: isDragging ? '#2F979E' : '#000',
                        }}
                      >
                        {isDragging
                          ? t('Déposez votre fichier ici')
                          : t('Glissez-déposez votre fichier Excel')}
                      </p>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#616161',
                          marginBottom: '20px',
                        }}
                      >
                        {t('ou cliquez pour parcourir')}
                      </p>

                      <button
                        type="button"
                        onClick={() => document.getElementById('smartBatchUpload').click()}
                        disabled={isLoading}
                        style={{
                          height: '40px',
                          width: '170px',
                          borderRadius: '8px',
                          backgroundColor: '#FFFFFF',
                          color: 'black',
                          border: '1px solid #CFCFCF',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        {t('Parcourir les fichiers')}
                      </button>

                      <p
                        style={{
                          fontSize: '12px',
                          color: '#616161',
                          marginTop: '15px',
                        }}
                      >
                        {t('Formats acceptés : .xlsx, .xls (max 10 Mo)')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    className="file-selected-container"
                    style={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '12px',
                      padding: '20px',
                      backgroundColor: '#FAFAFA',
                      marginTop: '20px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ marginTop: '-10px' }}>
                          <img
                            src="Images/icon-succ.png"
                            alt="Excel file"
                            style={{ width: '40px', height: '40px', marginTop: '-120px' }}
                          />
                        </div>

                        <div>
                          <p
                            style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              margin: 0,
                              color: '#000',
                            }}
                          >
                            {selectedFile.name}
                          </p>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              gap: '5px',
                            }}
                          >
                            <img
                              src="Images/check-icon.png"
                              alt="arrow-right"
                              style={{ width: '18px', height: '18px', marginTop: '5px' }}
                            />
                            <p
                              style={{
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#16A34A',
                                margin: 0,
                                marginTop: '4px',
                              }}
                            >
                              {filePreviewData?.length || 0} {t('contenus détectés')}
                            </p>
                          </div>

                          <p className="text-success1">
                            {t(
                              'Votre fichier a été analysé avec succès. Cliquez sur "Lancer l\'enrichissement" pour débuter le traitement.'
                            )}
                          </p>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'left',
                              alignItems: 'left',
                              marginTop: '10px',
                              gap: '8px',
                            }}
                          >

                            <button
                              type="button"
                              style={{
                                backgroundColor: '#FDFDFC',
                                color: 'black',
                                border: '1px solid #CFCFCF',
                                height: '40px',
                                width: '150px',
                                borderRadius: '8px',
                                marginTop: '10px',
                              }}
                            >
                              {t("Voir l'aperçu")}
                            </button>
                            <img
                              src="Images/imp-icon.png"
                              alt="arrow-right"
                              style={{ width: '20px', height: '20px', marginTop: '20px' }}
                            />
                            <button
                              type="button"
                              onClick={handleRemoveFile}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                marginTop: '13px',
                              }}
                            >
                              {t('Changer de fichier')}
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="close-button1"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#616161',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                        title={t('Supprimer le fichier')}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </TabPane>
            </TabContent>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={handleModalClose} disabled={isLoading}>
              {t('Annuler')}
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isLoading || !isFormValid()}
              style={{ opacity: !isFormValid() && !isLoading ? 0.6 : 1 }}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {activeTab === 'tab1' ? t('Traitement...') : t('Traitement du fichier...')}
                </>
              ) : (
                activeTab === 'tab1' ? t('Commencer l\'enrichissement') : t('Lancer l\'enrichissement')
              )}
            </Button>
          </ModalFooter>
        </Form>
        <GapGenerationModal
          isOpen={isGapModalOpen}
          onClose={() => setIsGapModalOpen(false)}
          mode={gapMode}
          onModeChange={setGapMode}
          keyword={formData.keyword}
          orderWebsite={orderWebsite}
          url={formData.url}
          language={language}
          country={country}
          contentScore={null}
          handleKeywordChange={handleSemanticGapChangeFromModal}
          onUpdate={() => { }}
          disableLightGap={gapAnalysisEnabled !== true}
        />

        <ProjectCreatedModal
          isOpen={isContentLimitModalOpen}
          onClose={() => setIsContentLimitModalOpen(false)}
          title={t('Limite de contenus atteinte')}
          message={t("Vous avez atteint le nombre maximum de contenus inclus dans votre forfait. Passez à une offre supérieure pour continuer à créer et optimiser vos contenus.")}
          confirmButtonText={t('Passer au forfait supérieur')}
          onConfirm={() => {
            setIsContentLimitModalOpen(false);
            if (!window.location.pathname.startsWith('/Consultant')) {
              window.location.assign('/AdminClient/Abonnement');
            }
          }}
          secondaryButtonText={t('Fermer')}
          onSecondaryAction={() => {
            setIsContentLimitModalOpen(false);
          }}
        />

        <ProjectCreatedModal
          isOpen={isCreditsLimitModalOpen}
          onClose={() => setIsCreditsLimitModalOpen(false)}
          title={t('Limite atteinte')}
          message={t("Vous avez atteint la limite autorisée par votre forfait. Rechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.")}
          confirmButtonText={t('Acheter des crédits')}
          onConfirm={() => {
            setIsCreditsLimitModalOpen(false);
            setIsBuyCreditsModalOpen(true);
          }}
          secondaryButtonText={t('Passer à une offre supérieure')}
          onSecondaryAction={() => {
            setIsCreditsLimitModalOpen(false);
          }}
        />

        <BuyCreditsModal open={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />

      </Modal>
    </>
  );
};

export default EnrichissementModal;
